import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  FaBook, FaRoute, FaUsers, FaChartLine, FaCalendarAlt,
  FaClipboardList, FaTrophy, FaStar, FaBolt
} from 'react-icons/fa';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement,
  ArcElement, Filler, Title, Tooltip, Legend
} from 'chart.js';
import StudentDashboard from './StudentDashboard';
import StudentProgressTab from './StudentProgressTab';
import axios from 'axios';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  ArcElement, Filler, Title, Tooltip, Legend
);

// ─── Count-up animation hook ────────────────────────────────────────────────
const useCountUp = (target, duration = 1200) => {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined) return;
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
};

// ─── Circular Progress Ring ─────────────────────────────────────────────────
const ProgressRing = ({ percent, size = 140, strokeWidth = 12, color = '#1086a4', label, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const [offset, setOffset] = useState(circumference);

  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(circumference - (percent / 100) * circumference);
    }, 100);
    return () => clearTimeout(timer);
  }, [percent, circumference]);

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke="#e5e7eb" strokeWidth={strokeWidth} fill="none"
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          stroke={color} strokeWidth={strokeWidth} fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.5s ease-out' }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-3xl font-bold text-[#054581]">{percent}%</span>
        {sublabel && <span className="text-xs text-gray-500">{sublabel}</span>}
      </div>
      {label && <span className="mt-2 text-sm font-medium text-gray-600">{label}</span>}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const DashboardContainer = ({ auth: { user, token: authToken } }) => {
  const [counts, setCounts] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  const animatedLessons = useCountUp(counts?.lessons ?? null);
  const animatedCourses = useCountUp(counts?.courses ?? null);
  const animatedPaths = useCountUp(counts?.paths ?? null);
  const animatedStudents = useCountUp(counts?.students ?? null);
  const animatedSubmissions = useCountUp(
    analytics ? Object.values(analytics.submissionBreakdown).reduce((a, b) => a + b, 0) : null
  );

  useEffect(() => {
    const headers = { Authorization: `Bearer ${authToken}` };
    axios.get('/api/dashboard/counts', { headers })
      .then(res => setCounts(res.data))
      .catch(() => setCounts(null));

    axios.get('/api/dashboard/analytics', { headers })
      .then(res => { setAnalytics(res.data); setAnalyticsLoading(false); })
      .catch(() => setAnalyticsLoading(false));
  }, [authToken]);

  if (user && user.role === 'student') return <StudentDashboard />;

  // ─── Chart data ──────────────────────────────────────────────────────────
  const enrollmentChartData = {
    labels: analytics?.enrollmentTimeline?.map(e => e.label) || [],
    datasets: [{
      label: 'New Students',
      data: analytics?.enrollmentTimeline?.map(e => e.count) || [],
      borderColor: '#1086a4',
      backgroundColor: 'rgba(16, 134, 164, 0.1)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#1086a4',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    }]
  };

  const enrollmentChartOptions = {
    plugins: { legend: { display: false }, tooltip: {
      backgroundColor: '#054581', titleColor: '#fff', bodyColor: '#fff', padding: 12, cornerRadius: 8
    }},
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 }, grid: { color: 'rgba(0,0,0,0.05)' } },
      x: { grid: { display: false } }
    },
    maintainAspectRatio: false,
  };

  const sb = analytics?.submissionBreakdown || {};
  const submissionChartData = {
    labels: ['Approved', 'Pending', 'Rejected', 'More Info'],
    datasets: [{
      data: [sb.approved || 0, sb.pending || 0, sb.rejected || 0, sb.more_info_needed || 0],
      backgroundColor: ['#10b981', '#f59e0b', '#ef4444', '#6366f1'],
      borderWidth: 0,
      cutout: '65%',
    }]
  };

  const submissionChartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#054581', titleColor: '#fff', bodyColor: '#fff', padding: 12, cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          }
        }
      }
    },
    maintainAspectRatio: false,
  };

  const totalSubs = Object.values(sb).reduce((a, b) => a + b, 0);

  // ─── Stat cards config ───────────────────────────────────────────────────
  const statCards = [
    { label: 'Total Students', value: counts ? animatedStudents : '—', icon: FaUsers, gradient: 'from-blue-500 to-blue-600', detail: analytics ? `${analytics.activeStudents} active` : null },
    { label: 'Total Courses', value: counts ? animatedCourses : '—', icon: FaRoute, gradient: 'from-teal-500 to-teal-600' },
    { label: 'Total Lessons', value: counts ? animatedLessons : '—', icon: FaBook, gradient: 'from-cyan-500 to-cyan-600' },
    { label: 'Learning Paths', value: counts ? animatedPaths : '—', icon: FaChartLine, gradient: 'from-indigo-500 to-indigo-600' },
    { label: 'Submissions', value: analytics ? animatedSubmissions : '—', icon: FaClipboardList, gradient: 'from-amber-500 to-amber-600' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-4 md:pt-8 px-3 sm:px-6 pb-20 md:pb-8">
      <div className="max-w-7xl mx-auto space-y-8">

        {/* ─── Header ──────────────────────────────────────────────────────── */}
        <div>
          <h1 className="text-3xl font-bold text-[#054581]">
            Welcome back, {user?.name || 'Admin'}
          </h1>
          <p className="text-gray-500 mt-1">Here's what's happening with your platform</p>
        </div>

        {/* ─── Row 1: Hero Stat Cards ──────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((card, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
                  <p className="text-2xl font-bold text-[#054581] mt-1">{card.value}</p>
                  {card.detail && (
                    <p className="text-xs text-green-600 font-medium mt-1">{card.detail}</p>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.gradient} flex items-center justify-center`}>
                  <card.icon className="text-white text-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Row 2: Charts ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Enrollment Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-1">Student Enrollment</h3>
            <p className="text-xs text-gray-400 mb-4">Monthly new student registrations</p>
            <div className="h-64">
              {analyticsLoading ? (
                <div className="h-full flex items-center justify-center text-gray-400">Loading chart...</div>
              ) : (
                <Line data={enrollmentChartData} options={enrollmentChartOptions} />
              )}
            </div>
          </div>

          {/* Submission Breakdown */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-1">Submission Status</h3>
            <p className="text-xs text-gray-400 mb-4">Breakdown of all submissions</p>
            <div className="flex items-center gap-6">
              <div className="relative w-48 h-48 flex-shrink-0">
                {analyticsLoading ? (
                  <div className="h-full flex items-center justify-center text-gray-400">...</div>
                ) : (
                  <Doughnut data={submissionChartData} options={submissionChartOptions} />
                )}
              </div>
              <div className="flex flex-col gap-3 text-sm">
                {[
                  { label: 'Approved', color: '#10b981', count: sb.approved || 0 },
                  { label: 'Pending', color: '#f59e0b', count: sb.pending || 0 },
                  { label: 'Rejected', color: '#ef4444', count: sb.rejected || 0 },
                  { label: 'More Info', color: '#6366f1', count: sb.more_info_needed || 0 },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></span>
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-semibold text-gray-800 ml-auto">{item.count}</span>
                    <span className="text-xs text-gray-400">
                      ({totalSubs > 0 ? ((item.count / totalSubs) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Row 3: Progress Tracking (Eye-Catching) ─────────────────────── */}
        <div>
          <h2 className="text-xl font-bold text-[#054581] mb-4 flex items-center gap-2">
            <FaBolt className="text-amber-500" />
            Progress Tracking
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Path Completion Gauge */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center relative">
              <h4 className="text-sm font-semibold text-gray-600 mb-4">Path Completion</h4>
              <div className="relative">
                <ProgressRing
                  percent={analytics?.pathCompletionRate ?? 0}
                  color="#1086a4"
                  sublabel="completed"
                />
              </div>
              <p className="text-xs text-gray-400 mt-3 text-center">
                {analytics?.pathCompletedStudents ?? 0} of {analytics?.totalStudents ?? 0} students
              </p>
            </div>

            {/* Active Students Gauge */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center relative">
              <h4 className="text-sm font-semibold text-gray-600 mb-4">Active Students</h4>
              <div className="relative">
                <ProgressRing
                  percent={analytics?.activeRate ?? 0}
                  color="#10b981"
                  sublabel="active"
                />
              </div>
              <div className="flex gap-4 mt-3 text-xs">
                <span className="text-green-600 font-medium">{analytics?.activeStudents ?? 0} active</span>
                <span className="text-gray-400">{analytics?.inactiveStudents ?? 0} inactive</span>
              </div>
            </div>

            {/* Average XP Meter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center">
              <h4 className="text-sm font-semibold text-gray-600 mb-4">Average XP</h4>
              <div className="flex flex-col items-center justify-center flex-1">
                <FaStar className="text-amber-400 text-4xl mb-2" />
                <span className="text-4xl font-bold text-[#054581]">
                  {analytics?.avgXP?.toLocaleString() ?? '—'}
                </span>
                <span className="text-xs text-gray-400 mt-1">avg per student</span>
                {analytics?.maxXP > 0 && (
                  <div className="w-full mt-4">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>0</span>
                      <span>{analytics.maxXP.toLocaleString()} (max)</span>
                    </div>
                    <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all duration-1000"
                        style={{ width: `${Math.min((analytics.avgXP / analytics.maxXP) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Top Performers */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h4 className="text-sm font-semibold text-gray-600 mb-4 flex items-center gap-2">
                <FaTrophy className="text-amber-500" />
                Top Performers
              </h4>
              {analyticsLoading ? (
                <div className="text-gray-400 text-sm">Loading...</div>
              ) : (
                <div className="space-y-3">
                  {(analytics?.topPerformers || []).map((performer, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? 'bg-amber-100 text-amber-700' :
                        i === 1 ? 'bg-gray-100 text-gray-600' :
                        i === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{performer.name}</p>
                        <p className="text-xs text-gray-400">Level {performer.level}</p>
                      </div>
                      <span className="text-sm font-bold text-[#054581]">{performer.xp?.toLocaleString()} XP</span>
                    </div>
                  ))}
                  {(!analytics?.topPerformers || analytics.topPerformers.length === 0) && (
                    <p className="text-sm text-gray-400">No data yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ─── Row 4: Student Progress Table ───────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <StudentProgressTab />
        </div>

        {/* ─── Row 5: Content Summary + Quick Actions ──────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Content Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-4">My Content</h3>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: 'Lessons', count: counts?.lessons ?? '—', icon: FaBook, path: '/content-management', color: 'text-cyan-600 bg-cyan-50' },
                { label: 'Courses', count: counts?.courses ?? '—', icon: FaRoute, path: '/content-management', color: 'text-teal-600 bg-teal-50' },
                { label: 'Paths', count: counts?.paths ?? '—', icon: FaChartLine, path: '/content-management', color: 'text-indigo-600 bg-indigo-50' },
              ].map((item, i) => (
                <Link
                  key={i}
                  to={item.path}
                  className="flex flex-col items-center p-4 rounded-xl border border-gray-100 hover:border-[#1086a4] hover:shadow-sm transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg ${item.color} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                    <item.icon className="text-lg" />
                  </div>
                  <span className="text-2xl font-bold text-[#054581]">{item.count}</span>
                  <span className="text-xs text-gray-500">{item.label}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-[#054581] mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/content-management"
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1086a4] hover:bg-[#1086a4]/5 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FaChartLine className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Manage Content</p>
                  <p className="text-xs text-gray-400">Create and edit courses, lessons, and paths</p>
                </div>
              </Link>
              <Link
                to="/events"
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1086a4] hover:bg-[#1086a4]/5 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
                  <FaCalendarAlt className="text-teal-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Schedule Session</p>
                  <p className="text-xs text-gray-400">Plan and manage upcoming sessions</p>
                </div>
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-[#1086a4] hover:bg-[#1086a4]/5 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <FaUsers className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">Manage Students</p>
                  <p className="text-xs text-gray-400">View and manage student accounts</p>
                </div>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });

export default connect(mapStateToProps)(DashboardContainer);
